import React, {useEffect, useRef} from 'react';
import {
    Button,
    Checkbox,
    Container,
    LinearProgress,
    TextField, Select, InputLabel, FormControl
} from '@material-ui/core';
import {Line} from 'react-chartjs-2';
import styles from '../styles/pages/Sentiment.module.css';
import {
    getSiteFilterData,
    getArticlesSentimentData,
    SiteFilterData,
    SentimentDataTO, getMinMaxPublishedDate
} from "../rest/clientV2";
import {ChartData, ChartDataSets, ChartOptions, ChartPoint} from 'chart.js';
import * as _ from 'lodash';
import MenuItem from "@material-ui/core/MenuItem";
import useMediaQuery from "../hooks/useMediaQuery";
import breakpoints from "../styles/Breakpoints.module.css";
import {zoomOptions} from '../helpers/chartZoom';
import 'chartjs-plugin-zoom';
import Input from "@material-ui/core/Input";
import ListItemText from "@material-ui/core/ListItemText";
import ArticleSentimentStatsTable, {ArticleSentimentStatsRow} from "../controls/ArticleSentimentStasTable";
import {SentimentResponseTO} from "../rest/client";

const ArticlesSentiment: React.FC = () => {
    const [data, setData] = React.useState<Map<number, SentimentDataTO[]>>(new Map<number, SentimentDataTO[]>());
    const [keywordData, setKeywordData] = React.useState<Map<number, SentimentDataTO[]>>(new Map<number, SentimentDataTO[]>());
    const [keyword, setKeyword] = React.useState<string>('');
    const [minYear, setMinYear] = React.useState<number>(2020);
    const [maxYear, setMaxYear] = React.useState<number>(2020);
    const [startMonth, setStartMonth] = React.useState<number>(1);
    const [startYear, setStartYear] = React.useState<number>(minYear);
    const [endYear, setEndYear] = React.useState<number>(maxYear);
    const [error, setError] = React.useState<string>();
    const [inProgress, setInProgress] = React.useState(false);
    const [allSites, setAllSites] = React.useState<SiteFilterData[]>([]);
    const [selectedSiteIds, setSelectedSiteIds] = React.useState<number[]>([]);
    const isMobileScreen = useMediaQuery(breakpoints.sm);

    const months = [
        {id: 1, name: 'January'},
        {id: 2, name: 'February'},
        {id: 3, name: 'March'},
        {id: 4, name: 'April'},
        {id: 5, name: 'May'},
        {id: 6, name: 'June'},
        {id: 7, name: 'July'},
        {id: 8, name: 'August'},
        {id: 9, name: 'September'},
        {id: 10, name: 'October'},
        {id: 11, name: 'November'},
        {id: 12, name: 'December'}
    ]


    useEffect(() => {
        let p1 = getSiteFilterData()
        let p2 = getMinMaxPublishedDate([])
        Promise.all([p1, p2]).then(data => {
            setAllSites(data[0])
            let dt = data[1]
            if (dt.min && dt.max) {
                let min = parseYear(dt.min)
                let max = parseYear(dt.max)
                setMinYear(min)
                setMaxYear(max)
                setStartYear(min)
                setEndYear(max)
            }
        })
    }, [])

    const parseYear = (s: string): number => Number(new Date(s).getFullYear())

    const sitesList = (): SiteFilterData[] => {
        if (allSites && allSites.length > 0) {
            let allSitesItem = {
                id: -1,
                url: "All"
            }
            return new Array(allSitesItem).concat(allSites.sort((a, b) => a.url.localeCompare(b.url)))
        } else {
            return []
        }
    }

    const canRunSearch = (): boolean => {
        return !inProgress && keyword.length > 0 && selectedSiteIds.length > 0
    }

    const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setKeyword(event.target.value);
    };
    const handleStartYearChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
        let year = Number(event.target.value);
        setStartYear(year)
    };
    const handleStartMonthChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
        let month = Number(event.target.value);
        setStartMonth(month)
    };
    const handleEndYearChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
        let year = Number(event.target.value);
        setEndYear(year)
    };
    const handleNewSearch = () => {
        if (startYear && endYear) {
            setError(undefined);
            setInProgress(true);
            let siteIds = Array.from(selectedSiteIds.values())
            let p1 = getArticlesSentimentData(startMonth, startYear, endYear, siteIds);
            let p2 = (keyword && keyword.length > 0)
                ? getArticlesSentimentData(startMonth, startYear, endYear, siteIds, keyword)
                : Promise.resolve([]);
            Promise.all([p1, p2]).then(data => {
                setData(new Map(data[0].map(i => [i.siteId, i.data])));
                setKeywordData(new Map(data[1].map(i => [i.siteId, i.data])));
                setInProgress(false);
            }).catch(err => {
                setError(err.toString());
                setInProgress(false);
            })
        }
    };

    const average = (arr: number[]): number => {
        return arr.length > 0
            ? _.sum(arr) / arr.length
            : 0
    }

    const getArticleSentimentStatsRows = (): ArticleSentimentStatsRow[] => {
        return selectedSiteIds.map(siteId => {
            let site = allSites.find(s => s.id === siteId)!
            let siteData = data.get(siteId) || []
            let kwData = keywordData.get(siteId) || []
            return ({
                siteUrl: site.url,
                fullUrl: formatSiteUrl(site.url),
                totalNumberOfPoints: siteData.length,
                totalAverageSentiment: average(siteData.map(a => a.sentiment)),
                keywordNumberOfPoints: kwData.length,
                keywordAverageSentiment: average(kwData.map(a => a.sentiment))
            })
        }).sort((a, b) => a.fullUrl.localeCompare(b.fullUrl));
    }


    let monthMenuItems = months.map(m => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)

    const yearMenuItems = _.range(minYear, maxYear + 1).map(i => <MenuItem key={i} value={i}>{i}</MenuItem>)

    let allSitesSelected = (): boolean => (selectedSiteIds.length === allSites.length)

    let onSiteChecked = (siteId: number, isChecked: boolean) => {
        if (siteId === -1) {
            let ids = isChecked ? allSites.map(a => a.id) : []
            setSelectedSiteIds(ids)
        } else {
            if (isChecked) {
                let ids = Array.from(selectedSiteIds)
                ids.push(siteId)
                setSelectedSiteIds(ids)
            } else {
                let ids = selectedSiteIds.filter(id => id !== siteId)
                setSelectedSiteIds(ids)
            }
        }
    }

    let siteCheckboxes = sitesList().map((site) => (
        <MenuItem key={site.id} value={site.url}>
            <Checkbox
                color="primary"
                checked={site.id === -1 && allSitesSelected()
                    ? true
                    : selectedSiteIds.includes(site.id)
                }
                onChange={(e, c) => {
                    onSiteChecked(site.id, c)
                }}
            />
            <ListItemText primary={formatSiteUrl(site.url)} onClick={() => {
                let isChecked = !((site.id === -1 && allSitesSelected()) || selectedSiteIds.includes(site.id))
                onSiteChecked(site.id, isChecked)
            }}/>
        </MenuItem>
    ))

    const chartRef = useRef<Line>(null);
    const handleZoomReset = () => {
        (chartRef.current?.chartInstance as any).resetZoom()
    }
    return <Container maxWidth='xl'>
        <div className={styles.chartContainer}>
            <Button
                variant="outlined"
                size="small"
                className={styles.resetZoom}
                color="primary"
                disabled={inProgress}
                onClick={handleZoomReset}>
                Reset Zoom
            </Button>
            <Line data={getChartData(data, keywordData, allSites)}
                  options={getChartOptions(isMobileScreen)}
                  ref={chartRef}
            />
        </div>
        {inProgress && <LinearProgress color="secondary" className={styles.progress}/>}
        {error && <span className={styles.error}>{error}</span>}
        <div className={styles.searchContainer}>
            <form className={styles.searchForm} noValidate autoComplete="off">
                <FormControl className={styles.formControl}>
                    <InputLabel id="demo-mutiple-checkbox-label">Sources</InputLabel>
                    <Select
                        labelId="demo-mutiple-checkbox-label"
                        id="demo-mutiple-checkbox"
                        multiple
                        disabled={allSites.length === 0}
                        value={selectedSiteIds}
                        renderValue={(selected) => (selected as number[]).length + ' selected'}
                        input={<Input/>}
                    >{siteCheckboxes}
                    </Select>
                </FormControl>
                <FormControl className={styles.formControl}>
                    <InputLabel id="start-month-select-label">Start Month</InputLabel>
                    <Select
                        labelId="start-month-select-label"
                        id="select-start-dt"
                        value={startMonth}
                        onChange={handleStartMonthChange}
                    >{monthMenuItems}</Select>
                </FormControl>
                <FormControl className={styles.formControl}>
                    <InputLabel id="start-dt-select-label">Start Year</InputLabel>
                    <Select
                        labelId="start-dt-select-label"
                        id="select-start-dt"
                        value={startYear}
                        onChange={handleStartYearChange}
                    >{yearMenuItems}</Select>
                </FormControl>
                <FormControl className={styles.formControl}>
                    <InputLabel id="end-dt-select-label">End Year</InputLabel>
                    <Select
                        labelId="end-dt-select-label"
                        id="select-end-dt"
                        value={endYear}
                        onChange={handleEndYearChange}
                    >{yearMenuItems}</Select>
                </FormControl>
            </form>
            <div className={styles.searchButtonContainer}>
                <TextField
                    id="outlined-basic"
                    label="Search text"
                    variant="outlined"
                    className={styles.searchControl}
                    value={keyword}
                    onChange={handleSearchTextChange}
                    onKeyPress={(e: React.KeyboardEvent<HTMLDivElement>) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            handleNewSearch();
                        }
                    }}
                />
                <div className={styles.searchButton}>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={!canRunSearch()}
                        onClick={handleNewSearch}>
                        Search
                    </Button>
                </div>
            </div>
        </div>
        <ArticleSentimentStatsTable rows={getArticleSentimentStatsRows()} showTotal={true}/>
    </Container>
}

export default ArticlesSentiment;

const convertData = (s: SentimentDataTO[]): ChartPoint[] => {
    return _.map(s, a => ({x: a.date, y: a.sentiment} as ChartPoint))
}

const formatSiteUrl = (url: string): string => {
    let schema = new RegExp("https?://")
    let tailSlash = new RegExp("/$")
    return url.replace(schema, "").replace(tailSlash, "")
}

const getChartData = (
    data: Map<number, SentimentDataTO[]>,
    keywordData: Map<number, SentimentDataTO[]>,
    sites: SiteFilterData[]
): ChartData => {
    let dataset: ChartDataSets[] = new Array<ChartDataSets>()
    data.forEach((d, siteId) => {
        let idx = dataset.length / 2
        let kwData = keywordData.get(siteId)
        if (kwData) {
            let ds = buildChartDataSets(siteId, d, false, sites, idx)
            let kwDs = buildChartDataSets(siteId, kwData, true, sites, idx)
            if (ds && kwDs) {
                dataset.push(ds)
                dataset.push(kwDs)
            }
        }
    })
    dataset.sort((a, b) => a.label!.localeCompare(b.label!))
    return {
        datasets: dataset
    }
}


const colors = [
    "#1A5AC3", "#E1331A", "#F59826", "#2E9424",
    "#981697", "#3A9AC4", "#DB4778", "#68A91D",
    "#B63033", "#346493", "#984797", "#3BA998",
    "#A8A728", "#663BC8", "#E4731F", "#890C10",
    "#641666", "#379163", "#5675A5", "#3C42A9",
    "#B5722C", "#46D432", "#B71D82", "#F13C9E",
    "#9B5939", "#AAC22D", "#2E778C", "#678C27",
    "#BDA229", "#195825", "#733417", "#3669C9",
    "#D93A21", "#F59826", "#2E9424", "#981697",
    "#3A9AC4", "#DB4778", "#68A91D", "#B63033",
]


const getColor = (idx: number): string => {
    return idx >= 0
        ? colors[idx % colors.length]
        : colors[0]
}

const lighten = (rgb: string): string => {
    let _rgb = rgb.substr(1)
    let R = parseInt("0x" + _rgb.substring(0, 2))
    let G = parseInt("0x" + _rgb.substring(2, 4))
    let B = parseInt("0x" + _rgb.substring(4, 6))

    let R2 = R + 20
    let G2 = G + 20
    let B2 = B + 20

    R2 = Math.min(R2, 255)
    G2 = Math.min(G2, 255)
    B2 = Math.min(B2, 255)

    let res = "#" + R2.toString(16) + G2.toString(16) + B2.toString(16)

    return res
}


const buildChartDataSets = (
    siteId: number,
    sentimentData: SentimentDataTO[],
    isKeyword: boolean,
    sites: SiteFilterData[],
    idx: number
): ChartDataSets | undefined => {
    let site = sites.find(s => s.id === siteId)
    if (site === undefined) {
        return undefined;
    }
    let formattedUrl = formatSiteUrl(site!.url)
    let color = getColor(idx)
    return (
        {
            data: convertData(sentimentData),
            label: formattedUrl + (isKeyword ? " & keyword" : ""),
            lineTension: 0,
            borderWidth: 2,
            borderColor: (isKeyword ? color : lighten(color)),
            // backgroundColor: '#112233',
            pointStyle: 'circle',
            radius: 1,
            borderDash: (isKeyword ? [] : [10, 2]),
            fill: false
        })
}

const getChartOptions = (isMobile?: boolean): ChartOptions => ({
    maintainAspectRatio: false,
    title: {
        display: true,
        text: 'Sentiment analysis'
    },
    scales: {
        xAxes: [{
            type: 'time',
            time: {
                unit: 'month',
                tooltipFormat: 'MMM DD, YYYY'
            }
        }],
        yAxes: [{
            ticks: {
                beginAtZero: true   // minimum value will be 0.
            }
        }]
    },
    legend: {
        position: isMobile ? "top" : "right",
        labels: {
            filter: (legendItem, chartData) => {
                return legendItem && legendItem.text && legendItem.text.length > 1
            }
        }
    },
    plugins: {zoom: zoomOptions}
})
